import React from 'react';

export const disorders = [
  // ============================================================
  // ADHD
  // ============================================================
  {
    slug: 'adhd',
    title: 'ADHD',
    fullName: 'Attention-Deficit / Hyperactivity Disorder',
    summary: 'A complex developmental condition that affects attention, judgement, decision-making, and activity levels — often misread as carelessness or laziness.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>ADHD is a complex <strong>developmental disorder</strong> of the brain. It has an impact on several domains of brain functioning such as attention, judgement, decision-making and activity levels. Individuals with ADHD are often labelled as being "lazy", "careless", "procrastinators" and "a nuisance to others" due to the condition.</p>
            <h4>Domains of ADHD</h4>
            <p>ADHD symptoms are categorised into two primary domains:</p>
            <p><strong>1. Inattention</strong> — includes:</p>
            <ul>
              <li>Difficulty sustaining attention</li>
              <li>Making repeated careless mistakes</li>
              <li>Struggling to organize tasks</li>
              <li>Poor time management</li>
              <li>Forgetfulness in daily activities</li>
              <li>Weak organizational skills</li>
            </ul>
            <p>Consequently, tasks requiring sustained focus are often avoided.</p>
            <p><strong>2. Hyperactivity-Impulsivity</strong> — includes:</p>
            <ul>
              <li>Difficulty remaining seated</li>
              <li>Inner restlessness and fidgeting</li>
              <li>Excessive talking</li>
              <li>Interrupting others</li>
              <li>Impatience</li>
              <li>Engaging in risky activities without considering the consequences</li>
            </ul>
            <p>These symptoms typically manifest early in life and can persist, causing challenges in academics, relationships, and the workplace. It can impact financial management and even sleep.</p>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'Symptom Progression',
        content: (
          <>
            <p>Initially thought to be solely a childhood disorder, ADHD is now recognised as a condition that can start or persist into adulthood for many individuals. Symptoms may improve with age, allowing some adults to function without significant impairment in daily life, but for many, predominantly attention-related symptoms continue into adult life.</p>
            <p>Although ADHD is more frequently diagnosed in males, females are often <strong>underdiagnosed</strong>. This is because females typically exhibit less hyperactivity and thereby receive less professional attention.</p>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'Varied Manifestations',
        content: (
          <>
            <p><strong>Adults</strong> struggle with:</p>
            <ul>
              <li>Chronic procrastination</li>
              <li>Difficulty with routines</li>
              <li>Burnout</li>
              <li>Difficulty managing emotions</li>
              <li>Workplace and relationship stress</li>
            </ul>
            <p><strong>Women</strong> with ADHD struggle with:</p>
            <ul>
              <li>Overthinking</li>
              <li>Emotional sensitivity and outbursts</li>
              <li>Feeling disorganized and forgetfulness</li>
              <li>Difficulty multi-tasking and managing responsibilities, especially with children</li>
            </ul>
          </>
        ),
      },
      {
        id: 'diagnosis',
        title: 'Diagnosis',
        content: (
          <>
            <p>The diagnostic process differs by age:</p>
            <ul>
              <li><strong>Children:</strong> Diagnosis involves reports from the child, parents, and teachers.</li>
              <li><strong>Adolescents and Adults:</strong> Diagnosis is primarily based on self-reported symptoms.</li>
            </ul>
          </>
        ),
      },
      {
        id: 'assessment-scales',
        title: 'Assessment Scales',
        content: (
          <>
            <ul>
              <li><strong>Conners Rating Scales</strong> — available in Parent, Teacher, and Self-report versions.</li>
              <li><strong>ADHD Rating Scale IV</strong> and <strong>DIVA 2.0</strong></li>
            </ul>
            <p>These scales aid in screening for ADHD likelihood, but a definitive diagnosis must be made by a mental health professional.</p>
          </>
        ),
      },
      {
        id: 'risks-if-untreated',
        title: 'Risks if Untreated',
        content: (
          <>
            <p>Without treatment, individuals with ADHD face increased risks, including:</p>
            <ul>
              <li>Severe academic and work decline</li>
              <li>Emotional breakdowns</li>
              <li>Substance abuse and addiction</li>
              <li>Engaging in risky behaviours, such as reckless driving and violence</li>
              <li>Depression, anxiety, and suicidal attempts</li>
            </ul>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <p><strong>1. Medication</strong> — Options include Methylphenidate, Atomoxetine, Clonidine, among others. The choice of medication is made collaboratively with the client, considering a risk-benefit analysis.</p>
            <p><strong>2. Therapy</strong> — Cognitive-behavioural therapy, cognitive training, problem-solving skills development, and parental training are effective therapeutic interventions.</p>
            <p><strong>3. Neuromodulation</strong> — Techniques such as transcranial Direct Current Stimulation (tDCS) offer additional support.</p>
            <p>By addressing ADHD with a collaborative and comprehensive treatment plan, individuals can significantly improve their quality of life and minimise the impact of symptoms on daily functioning.</p>
          </>
        ),
      },
      {
        id: 'when-to-seek-help',
        title: 'When to Seek Help',
        content: (
          <>
            <p>Consider seeking evaluation if difficulties with attention, organisation, impulsivity, or restlessness are:</p>
            <ul>
              <li>Affecting work or studies</li>
              <li>Causing relationship problems</li>
              <li>Leading to burnout or emotional distress</li>
              <li>Interfering with daily functioning</li>
              <li>Causing complications such as those listed above</li>
            </ul>
            <p>Early identification and support can make a significant difference.</p>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // ALCOHOL ADDICTION
  // ============================================================
  {
    slug: 'alcohol-addiction',
    title: 'Alcohol Addiction',
    fullName: 'Alcohol Use Disorder',
    summary: 'Loss of control over alcohol use that continues despite harm to health, relationships, and daily life — a long-lasting medical condition, not a lack of willpower.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Alcohol use is common among many cultures in India and is part of even some religious practices. Occasional or recreational use does not amount to a disorder.</p>
            <p>Alcohol Use Disorder is a condition in which a person loses control over alcohol use and continues to consume it heavily despite it causing problems in health, relationships, work, or daily life. The use continues despite not deriving any pleasure from it.</p>
            <p>Alcohol addiction can gradually cause lasting changes in the brain networks. <strong>It is not merely a lack of "willpower"</strong> but a long-lasting medical condition that has to be treated that way.</p>
            <p>Some people may drink daily, while others may binge drink heavily on certain occasions. Both patterns can become harmful.</p>
            <h4>Why do people become dependent on alcohol?</h4>
            <p>Alcohol addiction can develop due to a combination of:</p>
            <ul>
              <li>Genetic vulnerability</li>
              <li>Peer or social influences</li>
              <li>Coping mechanisms for stress, depression, and anxiety</li>
            </ul>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'Common Signs',
        content: (
          <>
            <p>A person may:</p>
            <ul>
              <li>Feel unable to control the amount of alcohol — i.e., loss of control</li>
              <li>Drink more than intended</li>
              <li>Need increasing amounts of alcohol to feel the same effect</li>
              <li>Urge to drink frequently</li>
              <li>Continue drinking despite harmful consequences</li>
              <li>Experience negative effects when alcohol is not taken</li>
            </ul>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'Complications of Alcohol Use',
        content: (
          <>
            <h4>Physical</h4>
            <ul>
              <li>Blackouts or memory gaps</li>
              <li>Injuries under intoxication — falls, accidents</li>
              <li>Shaking or tremors</li>
              <li>Fatigue</li>
              <li>Weight or appetite changes</li>
              <li>Gastric problems or liver-related symptoms</li>
              <li>Nutritional deficiency</li>
            </ul>
            <h4>Emotional and Behavioural Changes</h4>
            <ul>
              <li>Irritability or anger</li>
              <li>Anxiety or low mood</li>
              <li>Reduced motivation</li>
              <li>High-risk behaviours</li>
            </ul>
            <h4>Family and Relationships</h4>
            <ul>
              <li>Marital conflicts</li>
              <li>Domestic violence</li>
              <li>Financial stress</li>
            </ul>
            <h4>Work and Social Life</h4>
            <ul>
              <li>Reduced productivity</li>
              <li>Workplace absenteeism</li>
              <li>Social withdrawal</li>
              <li>Legal or social problems</li>
            </ul>
            <h4>What happens if alcohol use is suddenly stopped?</h4>
            <p>Over time, the body may become dependent on alcohol, and when alcohol intake suddenly reduces or stops, withdrawal symptoms can occur, such as sweating, tremors, anxiety, restlessness, nausea, and sleep disturbance.</p>
            <p>In severe cases, withdrawal can become dangerous and may cause confusion, hallucinations, or seizures. <strong>Medical supervision is important during the withdrawal phase</strong>, especially if complications have occurred in the past.</p>
          </>
        ),
      },
      {
        id: 'diagnosis',
        title: 'Diagnosis',
        content: (
          <>
            <p>Assessment usually involves understanding the pattern of use, complications and vulnerability to developing addiction. Patient's and family members' accounts are taken to make a diagnosis. Many people underestimate the severity of their drinking, which can delay treatment.</p>
          </>
        ),
      },
      {
        id: 'risks-if-untreated',
        title: 'Risks if Untreated',
        content: (
          <>
            <p>Without treatment, alcohol addiction may lead to:</p>
            <ul>
              <li>Liver disease and long-term health complications</li>
              <li>Relationship breakdown</li>
              <li>Financial difficulties</li>
              <li>Job loss</li>
              <li>Depression and anxiety</li>
              <li>Increased risk of accidents and injuries</li>
              <li>Cognitive and memory problems</li>
            </ul>
            <p>Over time, addiction may become more severe and harder to control.</p>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <h4>Detoxification and Withdrawal Management</h4>
            <p>Some individuals may require medically supervised detoxification to safely manage withdrawal symptoms.</p>
            <h4>Medications</h4>
            <p>Certain medications may help reduce cravings, prevent relapse, and manage withdrawal symptoms. Treatment is individualized based on the person's health and drinking pattern.</p>
            <h4>Psychotherapy and Counselling</h4>
            <p>Therapy can help individuals understand triggers, develop healthier coping skills, manage stress and emotions, improve motivation for recovery, and prevent relapse.</p>
            <h4>Family Involvement</h4>
            <p>Family support can play an important role in recovery. Education and counselling may help improve communication and reduce conflict.</p>
            <h4>Lifestyle and Recovery Support</h4>
            <p>Helpful recovery measures include regular routines, healthy sleep, exercise, stress management, avoiding triggers and high-risk situations, and building supportive social networks.</p>
            <p><strong>Recovery is possible.</strong> With proper treatment, support, and follow-up, many individuals regain emotional stability, rebuild relationships, improve physical health, and return to meaningful personal and professional lives.</p>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // ANXIETY DISORDERS
  // ============================================================
  {
    slug: 'anxiety',
    title: 'Anxiety Disorders',
    fullName: 'Generalised, Panic, Social & Phobic Anxiety',
    summary: 'Persistent worry, fear, or panic that disrupts sleep, focus, relationships, and daily life — among the most common, and most treatable, mental health conditions.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Most people worry from time to time — before an exam, an interview, speaking in public, or during important life events. But when worry becomes constant, overwhelming, and difficult to control, it may be an anxiety disorder.</p>
            <p>Anxiety is not simply "overthinking" or a personality trait. It is a real and treatable mental health condition.</p>
            <p>Anxiety disorders are among the most common mental health conditions worldwide. Nearly <strong>1 out of every 3 people</strong> may experience significant anxiety symptoms at some point in their life. Anxiety can affect people of any age and may continue for a long time if left untreated. It can also occur along with depression or other emotional difficulties.</p>
            <p>Common anxiety disorders include:</p>
            <ul>
              <li><strong>Generalized Anxiety Disorder (GAD)</strong> — constant excessive worry</li>
              <li><strong>Panic Disorder</strong> — sudden panic attacks</li>
              <li><strong>Social Anxiety Disorder</strong> — fear of social situations</li>
              <li><strong>Specific Phobias</strong> — intense fear of certain objects or situations</li>
            </ul>
            <p>Sometimes anxiety can feel like having a mind that never "switches off." Even while sitting at home, relaxing with family, or trying to sleep, the brain may continue anticipating problems, danger, embarrassment, or worst-case scenarios.</p>
            <p>Many people with anxiety continue going to work, attending classes, smiling, and managing responsibilities — while silently struggling internally with fear, tension, and constant worry.</p>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'What Anxiety Can Feel Like',
        content: (
          <>
            <p>People experiencing anxiety often describe feeling constantly "on edge," restless, or unable to relax even when there is no immediate danger.</p>
            <p>Common symptoms may include:</p>
            <ul>
              <li>Excessive worry or fear that feels difficult to control</li>
              <li>Racing thoughts or constant overthinking</li>
              <li>Feeling restless, tense, or easily startled</li>
              <li>Pounding heart, sweating, trembling, breathlessness, numbness, or dizziness</li>
              <li>Difficulty sleeping or relaxing</li>
              <li>Fear of social situations, travel, crowds, or specific places</li>
              <li>Avoiding situations because of fear or panic</li>
              <li>Difficulty concentrating because the mind feels "too busy"</li>
            </ul>
            <p>For some people, anxiety may suddenly become intense in the form of a <strong>panic attack</strong>, causing overwhelming fear along with physical symptoms such as chest tightness, palpitations, dizziness, or a feeling that "something terrible is about to happen."</p>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'How Anxiety Affects Daily Life',
        content: (
          <>
            <p>Over time, anxiety can affect both emotional and physical well-being. People may experience:</p>
            <ul>
              <li>Difficulty focusing at work or studies</li>
              <li>Reduced confidence and self-esteem</li>
              <li>Fear of public places or social situations</li>
              <li>Avoidance of travel, meetings, presentations, or crowds</li>
              <li>Constant stress, fatigue, or burnout</li>
              <li>Sleep difficulties and irritability</li>
              <li>Social withdrawal and strained relationships</li>
            </ul>
            <p>Many individuals appear calm externally while internally feeling overwhelmed by constant worry or fear.</p>
          </>
        ),
      },
      {
        id: 'assessment-scales',
        title: 'Assessment Scales',
        content: (
          <>
            <p>The <strong>GAD-7</strong> is a widely used self-report screening scale. Over the last 2 weeks, the scale asks how often a person has been bothered by:</p>
            <ul>
              <li>Feeling nervous, anxious, or on edge</li>
              <li>Not being able to stop or control worrying</li>
              <li>Worrying too much about different things</li>
              <li>Trouble relaxing</li>
              <li>Being so restless that it is hard to sit still</li>
              <li>Becoming easily annoyed or irritable</li>
              <li>Feeling afraid as if something awful might happen</li>
            </ul>
            <p>Each item is rated 0–3 (Not at all → Nearly every day). Total scores indicate severity:</p>
            <ul>
              <li><strong>0–4:</strong> Minimal anxiety</li>
              <li><strong>5–9:</strong> Mild anxiety</li>
              <li><strong>10–14:</strong> Moderate anxiety</li>
              <li><strong>15–21:</strong> Severe anxiety</li>
            </ul>
            <p>This self-assessment is only a screening tool and does not replace a professional mental health evaluation. If anxiety symptoms are persistent, distressing, or affecting daily life, seeking professional help may be beneficial.</p>
          </>
        ),
      },
      {
        id: 'risks-if-untreated',
        title: 'Risks if Untreated',
        content: (
          <>
            <p>Anxiety often worsens gradually when ignored or untreated. Early support can help reduce emotional distress and prevent avoidance behaviors from becoming more severe. Seeking help early may:</p>
            <ul>
              <li>Improve confidence and daily functioning</li>
              <li>Reduce physical and emotional stress</li>
              <li>Improve sleep and concentration</li>
              <li>Prevent worsening anxiety or associated depression</li>
              <li>Help individuals return comfortably to work, studies, and social life</li>
            </ul>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <h4>Psychotherapy</h4>
            <p>Therapies such as Cognitive Behavioral Therapy (CBT) help individuals understand anxiety patterns and develop healthier coping strategies.</p>
            <h4>Exposure Therapy</h4>
            <p>Gradual and supportive exposure-based therapy can help reduce fear and avoidance behaviors, especially in phobias, panic disorder, and social anxiety.</p>
            <h4>Medication</h4>
            <p>Medications may help when anxiety becomes severe, persistent, or significantly interferes with daily functioning.</p>
            <h4>Relaxation and Neuromodulation</h4>
            <p>Relaxation training, breathing exercises, mindfulness-based approaches, and treatments such as <strong>tDCS</strong> and <strong>TMS</strong> may help in selected cases.</p>
            <h4>VR Therapy for Phobias</h4>
            <p>Virtual Reality (VR)-based therapy can provide safe and controlled exposure experiences for individuals dealing with phobias and fear-related anxiety.</p>
          </>
        ),
      },
      {
        id: 'when-to-seek-help',
        title: 'When to Seek Help',
        content: (
          <p>With the right support and treatment, anxiety symptoms can improve significantly, and people can regain confidence and control over their lives. You do not have to manage anxiety alone — recovery from anxiety is possible, and even small steps toward seeking help can make a meaningful difference.</p>
        ),
      },
    ],
  },

  // ============================================================
  // AUTISM SPECTRUM DISORDER
  // ============================================================
  {
    slug: 'autism',
    title: 'Autism Spectrum Disorder',
    fullName: 'Autism Spectrum Disorder (ASD)',
    summary: 'A condition that affects how the brain develops and processes communication, social interaction, behaviour, and sensory information — a difference, not a disease.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Autism Spectrum Disorder, or simply Autism, is a condition in which the normal functions dictated by the brain — such as communication, social interaction, behaviour, and sensory processing — are affected.</p>
            <p>Autism is called a "spectrum" because symptoms and strengths can vary widely from person to person. Some people may communicate relatively normally while others may be non-verbal; some people may function without help while others require assistance in daily activities.</p>
            <p><strong>Autism is not a disease or a result of poor parenting.</strong> It is a difference in the way the brain develops and processes information.</p>
            <h4>What causes autism?</h4>
            <p>Autism is believed to involve a combination of genetic and neurodevelopmental factors. It is <strong>not</strong> caused by parenting style, emotional trauma, vaccinations, or lack of discipline. Families should not be blamed for a child's autism.</p>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'When Signs Appear',
        content: (
          <>
            <p>ASD begins before the age of 3 years and can last throughout the lifetime, although symptoms may change over time.</p>
            <p>Early signs may include:</p>
            <ul>
              <li>Delayed speech development</li>
              <li>Limited response to name</li>
              <li>Reduced eye contact</li>
              <li>Difficulty interacting with other children</li>
              <li>Repetitive play patterns, needing same routine</li>
              <li>Unusual reactions to sounds or textures</li>
            </ul>
            <p>Early identification and intervention can improve communication and adaptive skills.</p>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'Varied Manifestations',
        content: (
          <>
            <h4>Social and Communication Differences</h4>
            <p>A person with autism may find conversations challenging, avoid eye contact at times, have delayed speech or language development, and take language very literally. Some children may speak late, while others may be able to speak but with problems in social communication — i.e., when to speak, how much to speak, understanding gestures and body language.</p>
            <h4>Repetitive Behaviours and Routines</h4>
            <p>Individuals with autism may prefer fixed routines and structure, become distressed by sudden changes, repeat certain movements or phrases, and develop intense interests in specific topics.</p>
            <h4>Sensory Sensitivities</h4>
            <p>Many individuals with autism are sensitive to loud noises, bright lights, certain textures, crowded environments, or strong smells. Others may seek sensory stimulation through movement, touch, or repetitive activities.</p>
            <h4>Autism in Adults</h4>
            <p>Some individuals are diagnosed only in adulthood, especially those with milder symptoms or those who learned to "mask" difficulties socially. Adults with autism may experience social exhaustion, difficulty understanding relationships, sensory overwhelm, anxiety in social situations, and a preference for routines and predictability.</p>
            <p>Many adults with autism are highly intelligent, skilled, and capable, but may struggle in certain social or sensory environments.</p>
            <h4>Autism and Intelligence</h4>
            <p>Autism does not automatically mean intellectual disability — intelligence levels vary greatly. Many individuals with autism have strengths such as attention to detail, creativity, strong memory, logical thinking, and deep knowledge in specific areas.</p>
            <h4>Common Associated Difficulties</h4>
            <p>Some individuals with autism may also experience anxiety, ADHD, sleep problems, emotional regulation difficulties, learning difficulties, and sensory processing challenges.</p>
          </>
        ),
      },
      {
        id: 'diagnosis',
        title: 'Diagnosis',
        content: (
          <>
            <p>Thorough assessment of:</p>
            <ul>
              <li>Developmental history</li>
              <li>Communication and behaviour patterns</li>
              <li>Social interaction difficulties</li>
              <li>Clinical assessment by trained professionals</li>
            </ul>
            <p>In children, parents and teachers often notice symptoms first.</p>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment & Support',
        content: (
          <>
            <p>Early identification and intervention are very important for better outcomes. There is no single treatment or medication that can target all symptoms — interventions are individualised according to the deficits.</p>
            <h4>Behavioural and Developmental Therapies</h4>
            <p>Therapies may help improve communication skills, social interaction, emotional regulation, and adaptive functioning. Early intervention can be beneficial for many children.</p>
            <h4>Speech and Language Therapy</h4>
            <p>Speech therapy may help with language development, social communication, and understanding and expressing emotions.</p>
            <h4>Occupational Therapy</h4>
            <p>Occupational therapy may help individuals manage sensory sensitivities, daily living skills, and fine motor difficulties.</p>
            <h4>Parent and Family Support</h4>
            <p>Helping families understand autism can improve communication, reduce stress, and support the child more effectively. Families play a very important role, along with the therapist, in all interventions.</p>
            <h4>Acceptance and Support</h4>
            <p>Autism is not something that needs to be "cured." Many individuals with autism can thrive when given understanding, support, structure, and appropriate accommodations. Creating supportive environments at home, school, and work can significantly improve wellbeing and functioning.</p>
            <h4>Autism and School</h4>
            <p>Children with autism may benefit from structured routines, clear instructions, reduced sensory overload, individualised learning support, and emotional and behavioural understanding.</p>
          </>
        ),
      },
      {
        id: 'when-to-seek-help',
        title: 'When to Seek Help',
        content: (
          <>
            <p>Consider evaluation if there are:</p>
            <ul>
              <li>Delays in speech or social development</li>
              <li>Difficulty interacting with peers</li>
              <li>Repetitive or rigid behaviours</li>
              <li>Significant sensory sensitivities</li>
              <li>Persistent communication difficulties</li>
            </ul>
            <p>With understanding, early support, and inclusive environments, many individuals with autism lead meaningful, productive, and fulfilling lives. Focusing on strengths while supporting challenges can help improve confidence, independence, and quality of life.</p>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // BIPOLAR DISORDER
  // ============================================================
  {
    slug: 'bipolar',
    title: 'Bipolar Disorder',
    fullName: 'Bipolar Disorder (BD)',
    summary: 'A serious, episodic and recurrent mental illness that causes intense, long-lasting changes in mood, energy, and behaviour — far beyond typical "mood swings."',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Bipolar Disorder (BD) is a serious mental health illness that is episodic, long-term and recurrent, but with the right treatment, it can be managed effectively.</p>
            <p>It causes significant changes in mood, behaviour, thoughts, energy and overall functioning which are more intense and long-lasting than normal emotional ups and downs or "mood swings."</p>
            <p>Persons with BD experience episodes of:</p>
            <ol>
              <li><strong>Depression</strong> — enduring periods of low mood and reduced energy</li>
              <li><strong>Mania or hypomania</strong> — enduring periods of unusually high mood, increased energy and impulsive behaviour</li>
            </ol>
            <h4>Types</h4>
            <ul>
              <li><strong>Bipolar 1 disorder</strong> — involves at least one episode of full-blown mania</li>
              <li><strong>Bipolar 2 disorder</strong> — involves episodes of depression along with hypomania</li>
              <li><strong>Cyclothymia</strong> — a milder but long-term pattern of mood fluctuations of emotional highs and lows</li>
            </ul>
            <h4>Risk Factors</h4>
            <p>No single causative factor has been established; the interaction of genetic and environmental factors increases risk:</p>
            <ul>
              <li>Family members, especially close relatives, who have the illness</li>
              <li>Stressful life events, though not directly linked, may increase the risk</li>
            </ul>
            <p>Imbalances in several brain neurochemicals have been identified in BD.</p>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'What Bipolar Disorder Looks Like',
        content: (
          <>
            <h4>Depression in Bipolar Disorder</h4>
            <p>Symptoms are similar to those of depression due to any other cause:</p>
            <ul>
              <li>Persistent sadness or emptiness</li>
              <li>Loss of interest or pleasure</li>
              <li>Fatigue or low energy</li>
              <li>Poor concentration</li>
              <li>Feelings of guilt or hopelessness</li>
              <li>Sleep disturbances</li>
              <li>Changes in appetite</li>
              <li>Thoughts of self-harm or suicide</li>
            </ul>
            <h4>Mania or Hypomania</h4>
            <p>The person may:</p>
            <ul>
              <li>Feel unusually energetic or confident</li>
              <li>Talk more rapidly than usual</li>
              <li>Have racing thoughts</li>
              <li>Become highly productive and make grand, unrealistic plans</li>
              <li>Spend money impulsively or take more risks</li>
              <li>Feel unusually irritable or aggressive</li>
              <li>Need very little sleep</li>
            </ul>
          </>
        ),
      },
      {
        id: 'risks-if-untreated',
        title: 'Risks if Untreated',
        content: (
          <>
            <p>If left untreated, BD can affect:</p>
            <ul>
              <li>Work or academic performance</li>
              <li>Relationships</li>
              <li>Physical health</li>
              <li>Decision-making — substance abuse and risky behaviour</li>
            </ul>
            <p><strong>Without appropriate treatment, episodes may become more frequent and severe over time</strong> — highlighting the importance of early and consistent management.</p>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <p>After a thorough psychiatric assessment, individualised treatment in consultation with the patient and their family is initiated.</p>
            <h4>1. Medication</h4>
            <p>Medications are an important part of treatment and may help stabilise mood and reduce further episodes. They include:</p>
            <ul>
              <li>Mood stabilizers</li>
              <li>Certain antipsychotic medications</li>
              <li>Medications for associated symptoms such as anxiety or sleep problems</li>
            </ul>
            <h4>2. Psychotherapy</h4>
            <p>Therapy can help individuals:</p>
            <ul>
              <li>Understand mood patterns</li>
              <li>Manage stress</li>
              <li>Improve routines and sleep</li>
              <li>Build coping strategies</li>
              <li>Improve relationships and communication</li>
            </ul>
            <h4>3. Lifestyle Measures</h4>
            <ul>
              <li>Maintaining regular sleep schedules</li>
              <li>Avoiding alcohol and recreational drugs</li>
              <li>Staying physically active</li>
              <li>Taking medications consistently</li>
              <li>Monitoring early warning signs</li>
            </ul>
            <h4>Our Approach</h4>
            <ul>
              <li>Comprehensive and individualized psychiatric assessment</li>
              <li>Evidence-based treatment focused on long-term mood stability</li>
              <li>Emphasis on sleep regulation, routines, and relapse prevention</li>
              <li>Supportive, non-judgmental care involving families and caregivers where appropriate</li>
            </ul>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // DEPRESSION
  // ============================================================
  {
    slug: 'depression',
    title: 'Depression',
    fullName: 'Major Depressive Disorder',
    summary: 'A common, treatable condition that affects how a person feels, thinks, and lives — far more than occasional sadness, and not a sign of weakness or laziness.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Depression is a common and treatable mental health condition that affects the way a person feels, thinks, behaves, and experiences life. It is more than occasional sadness or stress and is <strong>not a sign of weakness, laziness, or lack of willpower</strong>.</p>
            <p>Depression is very common. Around <strong>7 out of every 100 people</strong> may experience depression in a year. It is seen more commonly in young adults and women.</p>
            <p>Everyone feels emotionally low, stressed, or sad at times. However, when these feelings continue for weeks, interfere with daily functioning, and begin affecting work, relationships, sleep, or overall quality of life, it may be depression.</p>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'What Depression Feels Like',
        content: (
          <>
            <p>People with depression often describe feeling like they are "not themselves anymore." Activities that once felt simple or enjoyable may begin to feel exhausting or meaningless.</p>
            <p>A person experiencing depression may notice:</p>
            <ul>
              <li>Persistent sadness, emptiness, or emotional numbness</li>
              <li>Feeling tired or drained even after rest</li>
              <li>Loss of interest in hobbies, socializing, or daily activities they previously enjoyed</li>
              <li>Changes in sleep — sleeping too much or too little</li>
              <li>Changes in appetite or weight</li>
              <li>Difficulty concentrating, remembering things, or making decisions</li>
              <li>Feelings of hopelessness, guilt, or worthlessness</li>
              <li>Reduced confidence and motivation</li>
              <li>Withdrawing from family, friends, or work</li>
              <li>In severe cases, thoughts of self-harm or wishing to die</li>
            </ul>
          </>
        ),
      },
      {
        id: 'assessment-scales',
        title: 'Assessment Scales',
        content: (
          <>
            <p>The <strong>PHQ-9</strong> is a widely used self-report scale. Over the last 2 weeks, the scale asks how often a person has been bothered by:</p>
            <ul>
              <li>Little interest or pleasure in doing things</li>
              <li>Feeling down, depressed, or hopeless</li>
              <li>Trouble sleeping or sleeping too much</li>
              <li>Feeling tired or having little energy</li>
              <li>Poor appetite or overeating</li>
              <li>Feeling bad about yourself</li>
              <li>Trouble concentrating</li>
              <li>Moving or speaking slowly, or feeling restless</li>
              <li>Thoughts of self-harm or feeling better off dead</li>
            </ul>
            <p>Each item is rated 0–3 (Not at all → Nearly every day). Total scores indicate severity:</p>
            <ul>
              <li><strong>0–4:</strong> Minimal symptoms</li>
              <li><strong>5–9:</strong> Mild depression</li>
              <li><strong>10–14:</strong> Moderate depression</li>
              <li><strong>15–19:</strong> Moderately severe depression</li>
              <li><strong>20–27:</strong> Severe depression</li>
            </ul>
            <p>This is a screening tool and does not replace a professional evaluation.</p>
          </>
        ),
      },
      {
        id: 'risks-if-untreated',
        title: 'Risks if Untreated',
        content: (
          <>
            <p>Depression can gradually worsen if left untreated. Early support can help reduce suffering, improve functioning, and prevent complications. Seeking help early may lead to:</p>
            <ul>
              <li>Faster recovery</li>
              <li>Better emotional well-being</li>
              <li>Improved relationships and work functioning</li>
              <li>Reduced risk of severe symptoms or self-harm</li>
            </ul>
            <p>Seeking help is a sign of strength, not weakness.</p>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <p>Depression is treatable, and recovery is possible with the right support. Treatment is personalized based on each person's symptoms and needs.</p>
            <h4>Psychiatric Consultation &amp; Medications</h4>
            <p>Antidepressants and other medications may help improve mood, sleep, energy, and emotional balance.</p>
            <h4>Psychotherapy</h4>
            <p>Therapies such as CBT and supportive therapy help individuals understand emotions, thoughts, and coping strategies.</p>
            <h4>Advanced Neuromodulation Treatments</h4>
            <p>Treatments such as <strong>rTMS</strong> and <strong>tDCS</strong> may be helpful in treatment-resistant depression, while <strong>ECT</strong> may be recommended in severe depression requiring faster improvement.</p>
            <h4>Ketamine Therapy</h4>
            <p>Ketamine-based treatments may help some individuals with treatment-resistant depression under specialist supervision.</p>
          </>
        ),
      },
      {
        id: 'when-to-seek-help',
        title: 'When to Seek Help',
        content: (
          <p>You do not have to go through depression alone. With the right support, recovery is possible. If feelings of sadness, hopelessness, or low energy continue for weeks and begin affecting daily life — at work, at home, or in relationships — reaching out is the first step.</p>
        ),
      },
    ],
  },

  // ============================================================
  // GRIEF AND PROLONGED GRIEF
  // ============================================================
  {
    slug: 'grief',
    title: 'Grief & Prolonged Grief',
    fullName: 'Grief and Prolonged Grief Disorder',
    summary: 'A natural human response to loss — but when grief stays intensely overwhelming for months or years, it may become Prolonged Grief Disorder, and support helps.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Losing someone important can deeply affect a person's life. The death of a partner, parent, child, close friend, or loved one can bring emotional pain, major life changes, and difficulty adjusting to daily routines.</p>
            <p>Grief is a natural human response to loss. For many people, the pain gradually becomes easier to carry with time, support, and healing. However, for some individuals, the grief remains intense and overwhelming for a long period, making it difficult to return to everyday life. This may be known as <strong>Prolonged Grief Disorder</strong>.</p>
            <h4>Grief is a Normal Human Reaction</h4>
            <p>There is no "right" or "wrong" way to grieve. Everyone experiences loss differently depending on their relationship with the person, personality, support system, and cultural or spiritual beliefs.</p>
            <p>Some people openly express emotions, while others become quiet or withdrawn. Some continue functioning normally on the outside while struggling internally.</p>
            <h4>Common Phases of Grief</h4>
            <p>People coping with grief may experience phases such as denial, anger, bargaining, sadness, and acceptance. These emotions are not fixed stages and may appear in different ways or return at different times. With time and support, most individuals gradually learn to adapt to the loss while continuing life forward.</p>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'How Prolonged Grief May Be Experienced',
        content: (
          <>
            <p>A person experiencing prolonged grief may feel emotionally "stuck" in the loss for months or even years. The grief may continue to feel as intense as it did in the beginning.</p>
            <p>People may:</p>
            <ul>
              <li>Constantly think about or long for the deceased person</li>
              <li>Feel unable to accept the loss</li>
              <li>Avoid reminders of the person, or become overly attached to them</li>
              <li>Feel emotionally numb, empty, or disconnected from others</li>
              <li>Struggle to find meaning, joy, or motivation in life</li>
              <li>Feel that a part of themselves has been lost</li>
              <li>Have difficulty returning to routines, relationships, or future plans</li>
            </ul>
            <p>Some individuals continue functioning outwardly, but internally feel overwhelmed by loneliness, guilt, or emotional exhaustion.</p>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'How Grief Affects Daily Life',
        content: (
          <p>Grief can affect relationships, sleep, concentration, work, studies, and physical health. People may withdraw socially, lose interest in activities, feel emotionally exhausted, or struggle with everyday responsibilities. Many individuals continue functioning outwardly, but only with significant emotional effort.</p>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment & Support',
        content: (
          <>
            <h4>Grief Counseling and Psychotherapy</h4>
            <p>Therapy provides a safe and compassionate space to process emotions, memories, guilt, or unresolved feelings related to the loss.</p>
            <h4>Emotional Support and Coping Strategies</h4>
            <p>Individuals are helped to gradually reconnect with routines, relationships, work, and meaningful activities.</p>
            <h4>Family and Social Support</h4>
            <p>Support from loved ones and community can play an important role in healing and recovery.</p>
            <h4>Treatment for Associated Symptoms</h4>
            <p>If grief is associated with depression, anxiety, sleep problems, or severe distress, additional treatment approaches and medications may sometimes help.</p>
            <p><em>Grief does not mean forgetting someone you loved. Healing means learning to carry the loss while slowly finding a way to live, connect, and hope again.</em></p>
          </>
        ),
      },
      {
        id: 'when-to-seek-help',
        title: 'When to Seek Help',
        content: (
          <>
            <p>Support may be helpful when:</p>
            <ul>
              <li>Intense grief continues for many months without improvement</li>
              <li>Daily life, work, or relationships become severely affected</li>
              <li>There is severe isolation, hopelessness, or emotional numbness</li>
              <li>The person feels unable to move forward after the loss</li>
              <li>Thoughts of self-harm or wishing to die are present</li>
            </ul>
            <p>Seeking help does not mean forgetting the loved one. It means receiving support in learning to live with the loss in a healthier and less overwhelming way.</p>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // OCD
  // ============================================================
  {
    slug: 'ocd',
    title: 'OCD',
    fullName: 'Obsessive-Compulsive Disorder',
    summary: 'A cycle of unwanted, distressing thoughts (obsessions) and repetitive behaviours (compulsions) that goes far beyond being "tidy" or "particular".',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Have you watched the movie <em>As Good as It Gets</em>? The character played by Jack Nicholson repeatedly checks locks, avoids touching objects, follows strict routines, and becomes extremely distressed when things do not happen in a particular way. While movies may dramatize symptoms, they also help people understand an important truth — <strong>OCD is not simply about being "too clean" or "too particular."</strong> It is a condition that can deeply affect a person's daily life, relationships, and peace of mind.</p>
            <p>Obsessive-Compulsive Disorder (OCD) is a common mental health condition that affects people across all age groups, including children and adolescents. Studies suggest that out of 100, nearly <strong>2–3 people</strong> experience OCD at some point in their lives.</p>
            <p>OCD mainly involves two types of symptoms:</p>
            <h4>Obsessions</h4>
            <p>Repeated thoughts, doubts, images, or urges that enter the mind again and again, even when the person does not want them to. These thoughts are unwanted, distressing, and difficult to ignore. People with OCD usually recognize that these thoughts are excessive or irrational, yet they often feel unable to stop them.</p>
            <h4>Compulsions</h4>
            <p>Repeated actions or mental rituals performed to reduce the anxiety caused by obsessive thoughts. A person may know that these actions are unnecessary or excessive, but resisting them can create intense discomfort. The relief from compulsions is usually temporary, causing the cycle to repeat again and again.</p>
            <h4>OCD is More Than a Habit</h4>
            <p>People often casually use phrases like "I'm so obsessed about cleanliness" or "I like things perfect." However, OCD is much more than being organized or detail-oriented. In OCD:</p>
            <ul>
              <li>Thoughts feel intrusive and distressing, and the person does not enjoy the process</li>
              <li>Rituals may consume significant time</li>
              <li>Symptoms interfere with daily functioning</li>
              <li>The person often feels trapped in a cycle of anxiety and relief</li>
            </ul>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'How OCD Affects Daily Life',
        content: (
          <>
            <p>OCD can gradually become emotionally exhausting and time-consuming. A person may:</p>
            <ul>
              <li>Spend hours caught in repetitive thoughts or rituals</li>
              <li>Avoid certain situations or places</li>
              <li>Struggle with work, studies, or relationships</li>
            </ul>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <p>With proper treatment, many people experience major improvement and are able to live meaningful, productive lives. Treatment may include:</p>
            <ul>
              <li><strong>Cognitive Behavioral Therapy (CBT)</strong>, especially Exposure and Response Prevention (ERP)</li>
              <li><strong>Medications</strong> (SSRI, SNRI, low-dose antipsychotics)</li>
              <li>Stress management and supportive routines</li>
              <li>Advanced Neuromodulation treatments like <strong>tDCS</strong> and <strong>TMS</strong></li>
            </ul>
            <p>Early treatment can make a significant difference. Living with OCD can feel exhausting, confusing, and lonely at times — but OCD is treatable, and support is available. With the right care, many people learn to manage symptoms effectively, regain confidence, and return to the things that matter most in their lives.</p>
          </>
        ),
      },
      {
        id: 'when-to-seek-help',
        title: 'When to Seek Help',
        content: (
          <>
            <p>It may be helpful to consult a mental health professional if:</p>
            <ul>
              <li>Repetitive thoughts or rituals are causing distress</li>
              <li>Symptoms are taking up a lot of time</li>
              <li>Daily life, work, studies, or relationships are being affected</li>
              <li>The person feels unable to control repetitive behaviors or thoughts</li>
            </ul>
            <p>Seeking help is an important step toward recovery and improved quality of life.</p>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // PERSONALITY DISORDERS
  // ============================================================
  {
    slug: 'personality-disorders',
    title: 'Personality Disorders',
    fullName: 'Personality Disorders (PDs)',
    summary: 'Long-standing patterns of thinking, feeling, and behaving that become rigid and distressing — affecting relationships, work, and how a person sees themselves.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Each one of us has distinct personality traits that make us unique, and these traits shape the way we deal with situations, people and our occupation.</p>
            <p>When these traits start interfering with work, the ability to form normal relationships, or cause personal distress, it is said to be a personality disorder.</p>
            <p>Personality disorders are conditions due to long-standing patterns of thinking, behaviours and emotions that are rigid and unhealthy, causing difficulties in all aspects of living. These patterns usually develop gradually over time and may affect how a person:</p>
            <ul>
              <li>Understands themselves and others</li>
              <li>Handles emotions</li>
              <li>Responds to stress</li>
              <li>Forms relationships</li>
              <li>Behaves in social situations</li>
            </ul>
            <p><strong>Personality disorders are not a sign of "bad character" or intentional behaviour.</strong> They are complex mental health conditions that can improve with proper support and treatment.</p>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'Different Types',
        content: (
          <>
            <p>Symptoms vary widely, and PDs have been grouped based on shared traits. Some traits described may be present in many of us — as long as they do not interfere with normal functioning, they do not constitute a Personality Disorder.</p>
            <h4>Emotionally Unstable / Borderline Personality Disorder (EUPD/BPD)</h4>
            <ul>
              <li>Rapid mood changes</li>
              <li>Intense impulsive relationships</li>
              <li>Fear of abandonment</li>
              <li>Impulsivity</li>
              <li>Sensitivity to rejection</li>
            </ul>
            <h4>Avoidant Personality Disorder</h4>
            <ul>
              <li>Social anxiety</li>
              <li>Fear of criticism</li>
              <li>Low confidence</li>
              <li>Avoidance of social situations</li>
            </ul>
            <h4>Obsessive Personality Disorder</h4>
            <ul>
              <li>Excessive perfectionism</li>
              <li>Need for control</li>
              <li>Rigidity in routines</li>
              <li>Difficulty delegating tasks</li>
            </ul>
            <h4>Paranoid Personality Disorder</h4>
            <ul>
              <li>Persistent mistrust</li>
              <li>Suspicion toward others</li>
              <li>Difficulty trusting relationships</li>
            </ul>
            <h4>Schizoid Personality Disorder</h4>
            <ul>
              <li>Preference for solitary activities</li>
              <li>Difficulty expressing emotions</li>
              <li>Indifference to praise or criticism</li>
              <li>Limited social relationships</li>
              <li>Feeling more comfortable in their own inner world</li>
            </ul>
            <h4>Antisocial Personality Disorder (ASPD)</h4>
            <ul>
              <li>Repeated lying or manipulation</li>
              <li>Aggressive or impulsive behaviour</li>
              <li>Lack of remorse after hurting others</li>
              <li>Irresponsibility in work or relationships</li>
              <li>Risk-taking behaviour</li>
              <li>Difficulty following laws or social norms</li>
            </ul>
            <h4>How Personality Disorders Affect Daily Life</h4>
            <p>Personality-related difficulties can affect relationships and marriages, work environment, social functioning, managing intense emotions, self-image and self-esteem, and ability to manage stress. People may often feel misunderstood, emotionally overwhelmed, or repeatedly experience conflict in relationships.</p>
            <h4>What Causes Personality Disorders?</h4>
            <p>There is no single cause. Since personality is shaped by genetics and environment, any deviations from normal experiences can contribute. Factors include genetic vulnerability, childhood experiences, emotional trauma or neglect, family environment, and temperament and coping styles. However, not everyone with difficult life experiences develops a personality disorder.</p>
            <h4>Common Associated Conditions</h4>
            <p>Personality disorders may occur along with anxiety disorders, depression, substance use problems, eating disorders, ADHD, and self-harm behaviours. A proper psychiatric assessment is important to understand overlapping symptoms.</p>
          </>
        ),
      },
      {
        id: 'diagnosis',
        title: 'Diagnosis',
        content: (
          <>
            <p>Diagnosis is based on:</p>
            <ul>
              <li>Long-term emotional and behavioural patterns</li>
              <li>Relationship difficulties</li>
              <li>Coping styles</li>
              <li>Personal history</li>
              <li>Impact on daily functioning</li>
            </ul>
            <p>Assessment usually involves detailed conversations and understanding patterns over time.</p>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <h4>Psychotherapy</h4>
            <p>Therapy is one of the most important parts of treatment and may help individuals understand emotional patterns, improve relationships, develop healthier coping skills, manage impulsivity, and improve self-esteem and emotional stability. Progress often occurs gradually over time.</p>
            <h4>Medications</h4>
            <p>There are no medications that "cure" personality disorders, but medications may help manage associated symptoms such as anxiety, depression, mood instability, and sleep problems.</p>
            <h4>Lifestyle and Supportive Measures</h4>
            <ul>
              <li>Building healthy routines</li>
              <li>Stress management</li>
              <li>Sleep regulation</li>
              <li>Emotional awareness</li>
              <li>Healthy communication skills</li>
              <li>Avoiding substance misuse</li>
            </ul>
            <h4>Importance of Support and Understanding</h4>
            <p>Many people with personality-related difficulties experience shame, rejection, or misunderstanding. A supportive, structured, and non-judgmental environment can help improve recovery and emotional well-being. Family understanding and healthy boundaries are often important parts of care.</p>
            <p><strong>Recovery and improvement are possible.</strong> With proper therapy, support, and self-awareness, many individuals can improve emotional regulation, relationships, coping skills, and overall quality of life. Change may take time, but meaningful improvement is possible.</p>
          </>
        ),
      },
      {
        id: 'when-to-seek-help',
        title: 'When to Seek Help',
        content: (
          <>
            <p>Consider seeking evaluation if there is:</p>
            <ul>
              <li>Frequent emotional crises</li>
              <li>Anxiety, anger, or low self-esteem that become difficult to manage</li>
              <li>Self-harm or suicidal thoughts</li>
              <li>Substance misuse</li>
              <li>Difficulty managing relationships and work</li>
            </ul>
            <p>Early interventions lead to better outcomes.</p>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // PMDD
  // ============================================================
  {
    slug: 'pmdd',
    title: 'PMDD',
    fullName: 'Premenstrual Dysphoric Disorder',
    summary: 'A severe, cyclical form of premenstrual mood and physical changes that wanes after the period — distinct from "normal PMS" and treatable as a mental health condition.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Some mood changes and physical discomfort are common among women 1–2 weeks before their periods.</p>
            <p>PMDD is a severe form of these premenstrual changes occurring cyclically in women of the reproductive age group that wane off after the period. The symptoms cause distress and impact daily functioning owing to their severity.</p>
            <p><strong>PMDD is a mental health condition that improves with treatment</strong> — not simply "Normal PMS."</p>
            <h4>Risk Factors</h4>
            <ul>
              <li>Pre-existing anxiety disorders or exposure to trauma</li>
              <li>Associated with smoking</li>
              <li>Obesity</li>
            </ul>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'Symptoms',
        content: (
          <>
            <p>Symptoms span three domains:</p>
            <h4>1. Emotional</h4>
            <ul>
              <li>Sudden mood swings</li>
              <li>Irritability or anger</li>
              <li>Feeling emotionally overwhelmed</li>
              <li>Anxiety or panic symptoms</li>
              <li>Crying spells</li>
              <li>Hopelessness or low mood</li>
              <li>Increased sensitivity to rejection or criticism</li>
            </ul>
            <h4>2. Cognitive</h4>
            <ul>
              <li>Brain fog or difficulty concentrating</li>
              <li>Lack of energy</li>
              <li>Overeating or craving specific foods</li>
              <li>Increased or decreased sleep</li>
              <li>Reduced motivation</li>
            </ul>
            <h4>3. Physical</h4>
            <ul>
              <li>Bloating</li>
              <li>Breast swelling or tenderness</li>
              <li>Headaches</li>
              <li>Body aches</li>
            </ul>
            <p>Symptoms can be experienced for a few days to 2 weeks leading up to the period. In many, they intensify about a week before and peak 2 days before menses — and must be present for at least 2 consecutive cycles.</p>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <ol>
              <li>Exercise and weight management</li>
              <li>Dietary changes — increased protein, calcium and Vitamin B6 intake has some evidence</li>
              <li>Stress management with meditation / breathing techniques</li>
              <li>Medication is suggested in severe cases — such as serotonin reuptake inhibitors and benzodiazepines, under supervision</li>
              <li>Hormonal therapies</li>
            </ol>
            <h4>Our Approach to PMDD Care</h4>
            <p>We provide collaborative, evidence-based treatment options based on the individual's needs:</p>
            <ul>
              <li>Detailed psychiatric assessment and supervision</li>
              <li>Psycho-education, CBT-based approaches and lifestyle guidance</li>
              <li>Medication if necessary</li>
              <li>Long-term strategies of management</li>
            </ul>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // SCHIZOPHRENIA
  // ============================================================
  {
    slug: 'schizophrenia',
    title: 'Schizophrenia',
    fullName: 'Schizophrenia',
    summary: 'A serious mental illness in which a person has difficulty distinguishing what is real from what is not — manageable with timely treatment and consistent support.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Schizophrenia is a serious mental illness that affects how a person thinks, understands reality, feels, and behaves. There is a breakdown of reality in patients, and they have difficulty in distinguishing what is real from what is not.</p>
            <p>It is a lifelong condition that cannot be cured but can be controlled with the right treatment. <strong>Early diagnosis and treatment are very important for better outcomes.</strong></p>
            <h4>When and in whom is Schizophrenia seen?</h4>
            <p>It is seen more commonly in men than women and starts by early adulthood in most patients. There is no single causative factor, but it is common in those with:</p>
            <ul>
              <li>Genetic risk — having close relatives with schizophrenia</li>
              <li>Complications in childbirth, adverse experiences in childhood</li>
            </ul>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'What Schizophrenia Looks Like',
        content: (
          <>
            <h4>1. Positive Symptoms</h4>
            <p>The most dramatic symptoms seen in most patients. They include:</p>
            <ul>
              <li><strong>Hallucinations</strong> — hearing voices others cannot hear; seeing, feeling, or sensing things that are not real</li>
              <li><strong>Delusions</strong> — feeling watched or under threat, giving special meaning to ordinary events</li>
              <li><strong>Disorganised thinking and speech</strong> — jumping topics, easy distractibility, poor decision-making</li>
            </ul>
            <h4>2. Negative Symptoms</h4>
            <p>Represent the loss of normal functions in an individual:</p>
            <ul>
              <li>Reduced emotional expression</li>
              <li>Loss of motivation</li>
              <li>Reduced self-care</li>
              <li>Poor social interaction</li>
              <li>Difficulty functioning at work or school</li>
            </ul>
            <p>There may be early warning signs that are subtle changes in personality, which when picked up early and treated can lead to better outcomes.</p>
          </>
        ),
      },
      {
        id: 'risks-if-untreated',
        title: 'Risks if Untreated',
        content: (
          <>
            <p>If left untreated, schizophrenia causes:</p>
            <ul>
              <li>Progressive difficulty in work, studies, or daily functioning</li>
              <li>Social withdrawal and relationship difficulties</li>
              <li>Decline in personal care and hygiene</li>
              <li>Substance or alcohol misuse as a coping mechanism</li>
              <li>Risk of anxiety, depression, and suicide</li>
              <li>Reduced insight into illness, making treatment more difficult</li>
            </ul>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <h4>1. Medication</h4>
            <p>Antipsychotic medications are often important in reducing symptoms such as hallucinations, suspiciousness, and disorganised thinking. Treatment plans are individualised and monitored carefully.</p>
            <h4>2. Psychotherapy and Rehabilitation</h4>
            <p>Supportive therapy can help with coping with symptoms, improving routines, social and communication skills, and insight and recovery planning. Rehabilitation and occupational support may also improve functioning and independence.</p>
            <h4>3. Family Support and Psychoeducation</h4>
            <p>Family understanding plays an important role in recovery. Learning about schizophrenia can reduce conflict, improve communication, and help families respond more effectively during difficult periods.</p>
            <h4>4. Lifestyle and Routine Support</h4>
            <ul>
              <li>Avoiding alcohol and recreational drugs</li>
              <li>Maintaining daily structure</li>
              <li>Medication adherence</li>
              <li>Social support</li>
            </ul>
            <h4>Our Approach</h4>
            <ul>
              <li>Comprehensive psychiatric assessment with individualised treatment planning</li>
              <li>Evidence-based management focused on symptom control, stability, and long-term recovery</li>
              <li>Psychoeducation and family support to improve understanding and communication</li>
              <li>Regular follow-ups, relapse prevention and medication monitoring</li>
              <li>Rehabilitation-focused care aimed at improving daily functioning, routines, and quality of life</li>
            </ul>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // STRESS-RELATED DISORDERS
  // ============================================================
  {
    slug: 'stress',
    title: 'Stress-Related Disorders',
    fullName: 'Burnout, Adjustment & Trauma-Related Stress',
    summary: 'When stress becomes constant, overwhelming, or difficult to cope with, it affects both mind and body — presenting as burnout, adjustment difficulties, or trauma responses.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Stress is a normal part of life. However, when stress becomes constant, overwhelming, or difficult to cope with, it can begin to affect both mental and physical well-being.</p>
            <p>Stress-related difficulties may develop due to ongoing work pressure, relationship challenges, major life changes, caregiving responsibilities, academic stress, or traumatic experiences. These may present as burnout, adjustment difficulties, or trauma-related emotional responses.</p>
            <p><strong>Feeling emotionally exhausted all the time is not something you simply have to "push through."</strong></p>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'What Stress-Related Difficulties Feel Like',
        content: (
          <>
            <p>People experiencing prolonged stress often describe feeling mentally and physically drained, emotionally overwhelmed, or unable to "switch off" their thoughts.</p>
            <p>Common symptoms may include:</p>
            <ul>
              <li>Constant worry, tension, or feeling overwhelmed</li>
              <li>Irritability or frustration over small things</li>
              <li>Feeling emotionally exhausted or "burnt out"</li>
              <li>Fatigue and low energy</li>
              <li>Difficulty sleeping or poor-quality sleep</li>
              <li>Headaches, muscle tension, or body aches</li>
              <li>Reduced motivation and productivity</li>
              <li>Difficulty concentrating or making decisions</li>
              <li>Feeling emotionally numb or detached</li>
              <li>Increased sensitivity to stress or criticism</li>
            </ul>
            <p>Some individuals may also experience emotional distress after difficult or traumatic life events.</p>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'How Stress Affects Daily Life',
        content: (
          <>
            <p>When stress continues for long periods, it can affect emotional health, relationships, work performance, and physical health. People may experience:</p>
            <ul>
              <li>Reduced efficiency at work or studies</li>
              <li>Emotional exhaustion and burnout</li>
              <li>Increased conflicts in relationships</li>
              <li>Physical symptoms such as headaches, fatigue, stomach issues, or body pain</li>
            </ul>
            <p>Over time, unmanaged stress can leave people feeling constantly "stuck in survival mode."</p>
          </>
        ),
      },
      {
        id: 'risks-if-untreated',
        title: 'Risks if Untreated',
        content: (
          <>
            <p>Ignoring chronic stress may eventually affect both mental and physical health. Without support, prolonged stress can contribute to:</p>
            <ul>
              <li>Burnout and emotional exhaustion</li>
              <li>Anxiety or depression</li>
              <li>Sleep difficulties</li>
              <li>Relationship problems</li>
              <li>Reduced work performance</li>
              <li>Physical health complications such as headaches, fatigue, and body pain</li>
            </ul>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <p>With the right support, people can learn healthier coping strategies, improve resilience, and regain emotional balance.</p>
            <h4>Psychotherapy</h4>
            <p>Therapy provides a safe space to understand stress, process emotions, and develop healthier ways of coping.</p>
            <h4>Stress-Management Techniques</h4>
            <p>Relaxation training, mindfulness, breathing exercises, and emotional regulation strategies can help reduce mental and physical stress.</p>
            <h4>Lifestyle Interventions</h4>
            <p>Improving sleep, physical activity, routine, nutrition, and work-life balance can significantly improve stress-related symptoms.</p>
            <h4>Resilience Building</h4>
            <p>Therapy can help individuals build emotional resilience, confidence, and healthier responses to future challenges.</p>
            <p><strong>You do not have to manage overwhelming stress alone.</strong> Support and recovery are possible.</p>
          </>
        ),
      },
    ],
  },

  // ============================================================
  // TOBACCO ADDICTION
  // ============================================================
  {
    slug: 'tobacco',
    title: 'Tobacco Addiction',
    fullName: 'Tobacco Use Disorder',
    summary: 'Nicotine dependence from smoking or smokeless tobacco — one of the most addictive substances of abuse, with serious effects on physical and mental health.',
    sections: [
      {
        id: 'understanding',
        title: 'Understanding',
        content: (
          <>
            <p>Tobacco use is common in India in both smoking (cigarettes/bidis) and smokeless forms. Nicotine is the addictive substance found in cigarettes, bidis, cigars, hookah, vaping products, and smokeless tobacco such as gutka, khaini, pan masala, and chewing tobacco — and is one of the most addictive substances of abuse.</p>
            <p>Tobacco Use Disorder is a condition in which a person becomes dependent on nicotine — i.e., continues to use despite negative health consequences.</p>
            <h4>Common Forms of Tobacco Use in India</h4>
            <p><strong>1. Smoking forms:</strong></p>
            <ul>
              <li>Cigarettes</li>
              <li>Bidis</li>
              <li>Hookah</li>
              <li>Cigars</li>
              <li>E-cigarettes or vaping products</li>
            </ul>
            <p><strong>2. Smokeless tobacco:</strong></p>
            <ul>
              <li>Gutka and pan masala containing tobacco</li>
              <li>Khaini and zarda</li>
              <li>Hookah</li>
              <li>Chewing tobacco</li>
            </ul>
            <p>Many people believe smokeless tobacco or occasional smoking is safer, but <strong>all forms of tobacco can be harmful and addictive</strong>.</p>
          </>
        ),
      },
      {
        id: 'symptom-progression',
        title: 'Signs of Tobacco Addiction',
        content: (
          <>
            <p>A person may:</p>
            <ul>
              <li>Feel strong cravings for tobacco, as soon as waking up</li>
              <li>Find it difficult to stop or cut down</li>
              <li>Feel restless or irritable without tobacco</li>
              <li>Continue using despite health problems</li>
              <li>Use tobacco to cope with stress or emotions</li>
            </ul>
            <p>Repeated failed attempts to quit may indicate dependence.</p>
            <h4>Withdrawal Symptoms</h4>
            <p>When a person reduces or stops tobacco use, withdrawal symptoms may occur, such as irritability, anxiety or restlessness, difficulty concentrating, increased appetite, mood changes, sleep disturbance, and strong cravings. These symptoms are temporary and worse in the first few weeks of quitting — they subside over time with treatment and support.</p>
          </>
        ),
      },
      {
        id: 'varied-manifestations',
        title: 'How Tobacco Affects Health',
        content: (
          <>
            <h4>Physical Health Risks</h4>
            <p>Tobacco use increases the risk of:</p>
            <ul>
              <li>Oral cancer</li>
              <li>Lung disease — COPD, worsens asthma, chronic cough</li>
              <li>Heart disease</li>
              <li>Stroke</li>
              <li>High blood pressure</li>
              <li>Dental and gum disease</li>
            </ul>
            <p>Smokeless tobacco commonly used in India can also cause severe oral health problems and cancers.</p>
            <h4>Mental Health Effects</h4>
            <p>Nicotine may temporarily feel calming, but long-term tobacco use can worsen anxiety, stress, sleep problems, and mood instability. Many people feel trapped in a cycle of stress and nicotine cravings.</p>
            <h4>Effects on Family and Others</h4>
            <p>Smoking can affect the health of people nearby, especially children, pregnant women, and elderly family members. Second-hand smoke exposure increases the risk of respiratory and heart-related illnesses.</p>
            <h4>Why is Quitting Difficult?</h4>
            <p>Nicotine hijacks the brain's reward system, causing unpleasant effects on quitting. Many people continue tobacco use because of habit and routine, stress-relief attempts, social environment, emotional triggers, or fear of withdrawal symptoms. Professional support can improve the chances of successful quitting.</p>
          </>
        ),
      },
      {
        id: 'treatment',
        title: 'Treatment',
        content: (
          <>
            <h4>Nicotine Replacement Therapy (NRT)</h4>
            <p>Many individuals benefit from nicotine replacement to reduce unpleasant withdrawal symptoms and cravings — including nicotine gums, patches, and lozenges.</p>
            <h4>Medications</h4>
            <p>Certain medications may help reduce cravings and improve quit success rates under medical supervision.</p>
            <h4>Behavioural Counselling</h4>
            <p>Counselling can help individuals identify triggers, manage cravings, build coping skills, prevent relapse, and stay motivated during recovery.</p>
            <h4>Lifestyle Changes</h4>
            <p>Helpful strategies include avoiding triggers, regular exercise, stress management, building supportive routines, and seeking support from family or friends.</p>
            <h4>Benefits of Quitting Tobacco</h4>
            <p>Health benefits begin within days to weeks of quitting and continue over time. Quitting tobacco can improve breathing and stamina, heart health, sleep, taste and smell, energy levels, and oral health. It also reduces health risks for family members exposed to smoke and improves overall quality of life.</p>
            <p><strong>Recovery is possible.</strong> Quitting tobacco can be challenging, but recovery is achievable with proper support, treatment, and motivation. Repeated attempts at quitting are common — therefore, must not be viewed as failures.</p>
          </>
        ),
      },
      {
        id: 'when-to-seek-help',
        title: 'When to Seek Help',
        content: (
          <>
            <p>Consider seeking support if:</p>
            <ul>
              <li>You have tried quitting multiple times unsuccessfully</li>
              <li>Cravings interfere with daily functioning</li>
              <li>Tobacco use is becoming more frequent</li>
              <li>You rely on tobacco to manage stress or emotions</li>
              <li>Family members are concerned about your tobacco use</li>
            </ul>
            <p>Seeking help early can improve long-term success in quitting.</p>
          </>
        ),
      },
    ],
  },
];

export const findDisorder = (slug) => disorders.find((d) => d.slug === slug);
